/** La única puerta al backend.
 *
 * Dos cosas que no se hacen en ningún otro sitio:
 *
 * - **Los errores se traducen aquí.** El backend devuelve 422 con un texto escrito para leerse;
 *   eso se lanza tal cual. Lo demás lleva un mensaje genérico, porque un `TypeError: failed to
 *   fetch` no le dice nada a nadie.
 * - **Los identificadores de Discord son cadenas** de punta a punta. Pasan de 2^53 y convertirlos
 *   a número los redondea en silencio.
 */

export class ErrorDeApi extends Error {
  constructor(
    mensaje: string,
    readonly codigo: number,
  ) {
    super(mensaje)
    this.name = 'ErrorDeApi'
  }

  /** Sesión caducada o nunca iniciada: la interfaz lleva a la pantalla de entrar. */
  get hayQueEntrar() {
    return this.codigo === 401
  }
}

async function pedir<T>(ruta: string, opciones: RequestInit = {}): Promise<T> {
  let respuesta: Response
  try {
    respuesta = await fetch(`/api${ruta}`, {
      credentials: 'same-origin',
      // Con un formulario con archivos no se pone el tipo: el navegador añade el suyo, con el
      // separador que hace falta, y uno puesto a mano lo rompería.
      headers: opciones.body && !(opciones.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {},
      ...opciones,
    })
  } catch {
    throw new ErrorDeApi('No se ha podido conectar con el servidor.', 0)
  }

  if (respuesta.status === 204) return undefined as T

  let cuerpo: unknown = null
  try {
    cuerpo = await respuesta.json()
  } catch {
    cuerpo = null
  }

  if (!respuesta.ok) {
    throw new ErrorDeApi(mensajeDe(cuerpo, respuesta.status), respuesta.status)
  }
  return cuerpo as T
}

function mensajeDe(cuerpo: unknown, codigo: number): string {
  const detalle = (cuerpo as { detail?: unknown } | null)?.detail
  if (typeof detalle === 'string') return detalle
  // FastAPI devuelve una lista cuando el cuerpo no valida; se enseña el primer problema.
  if (Array.isArray(detalle) && detalle.length > 0) {
    const primero = detalle[0] as { msg?: string; loc?: unknown[] }
    const donde = Array.isArray(primero.loc) ? primero.loc.slice(1).join('.') : ''
    return donde ? `${donde}: ${primero.msg ?? 'valor no válido'}` : (primero.msg ?? 'Datos no válidos.')
  }
  if (codigo === 401) return 'Tu sesión ha caducado. Vuelve a entrar.'
  if (codigo === 403) return 'No tienes permiso para esto.'
  if (codigo === 404) return 'Eso no existe o no tienes acceso.'
  if (codigo === 429) return 'Vas demasiado rápido. Espera un momento.'
  return 'Algo ha fallado por nuestra parte.'
}

export const api = {
  get: <T>(ruta: string) => pedir<T>(ruta),
  post: <T>(ruta: string, cuerpo?: unknown) =>
    pedir<T>(ruta, { method: 'POST', body: JSON.stringify(cuerpo ?? {}) }),
  patch: <T>(ruta: string, cuerpo: unknown) =>
    pedir<T>(ruta, { method: 'PATCH', body: JSON.stringify(cuerpo) }),
  put: <T>(ruta: string, cuerpo: unknown) =>
    pedir<T>(ruta, { method: 'PUT', body: JSON.stringify(cuerpo) }),
  borrar: <T>(ruta: string) => pedir<T>(ruta, { method: 'DELETE' }),
  subir: <T>(ruta: string, formulario: FormData) =>
    pedir<T>(ruta, { method: 'POST', body: formulario }),
}

/** El prefijo de todo lo del portal de un servidor. */
export const portal = (guildId: string) => `/guilds/${guildId}/portal`
